import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

// Importaciones de entornos.
import { environment } from '../../../environments/environment';

// Importaciones de servicios.
import { HostService } from '../../services/host.service';
import { LogService } from '../../services/log.service';

// Importaciones de interfaces.
import { IHost } from '../../interfaces/host.interface';

// Importaciones de modelos.
import { Log } from '../../models/log.model';

/**
 * Componente de diálogo para la eliminación del host.
 *
 * Este componente proporciona una interfaz para confirmar y realizar la eliminación del host.
 * Se muestra como un cuadro de diálogo, permitiendo al usuario confirmar la acción antes de proceder con la eliminación.
 */
@Component({
  selector: 'app-host-delete-dialog',
  templateUrl: './host-delete-dialog.component.html',
  standalone: false,
})
export class HostDeleteDialogComponent {
  /**
   * Referencia al diálogo actual.
   *
   * Se utiliza para controlar y cerrar el cuadro de diálogo de eliminación del host.
   */
  readonly dialogRef: MatDialogRef<HostDeleteDialogComponent> = inject(
    MatDialogRef<HostDeleteDialogComponent>
  );

  /**
   * Datos del host proporcionados al diálogo.
   *
   * Se inyectan desde MAT_DIALOG_DATA y son de solo lectura.
   */
  private readonly host: IHost = inject<IHost>(MAT_DIALOG_DATA);

  /**
   * Indica si la operación de eliminado está en curso.
   *
   * Se utiliza para deshabilitar la interfaz y mostrar un indicador de carga mientras se elimina el host.
   */
  loading: boolean = false;

  /**
   * Lista de errores.
   *
   * Contiene los mensajes de error generados si la operación de eliminado falla.
   */
  errors: string[] = [];

  /**
   * Constructor del componente.
   *
   * Este constructor inyecta las dependencias necesarias para el funcionamiento del componente.
   *
   * @param hostService - Servicio para gestionar la información de los hosts.
   * @param logService - Servicio para gestionar los registros.
   * @param snackBar - Servicio para mostrar notificaciones de tipo snack bar.
   */
  constructor(
    private hostService: HostService,
    private logService: LogService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Confirma y elimina el host actual.
   *
   * Muestra un diálogo de confirmación antes de proceder con la eliminación.
   * Si la eliminación es exitosa, cierra el diálogo.
   */
  async delete(): Promise<void> {
    this.loading = true;
    this.errors = [];

    try {
      const hostResponse: string = await this.hostService.delete(this.host);

      // Añadir un registro de eliminación del host.
      try {
        await this.logService.create(
          new Log({
            description: `Ha eliminado el host '${this.host.id}'.`,
          })
        );
      } catch (errors: any) {
        if (!environment.production) {
          console.warn(
            'No se ha podido guardar el registro de eliminación del host.'
          );
        }
      }

      this.snackBar.open(hostResponse, '', { duration: 5000 });

      this.dialogRef.close(true);
    } catch (errors: any) {
      this.errors = errors;
    }

    this.loading = false;
  }
}
